<script setup lang="tsx">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/shared/Modal.vue'
import RiskCatalogSelect from '../risk_management/matrix/RiskCatalogSelect.vue'
import type { RiskCatalogMeta, Threat, ThreatMeta } from '@/models/api-generated'
import { newDefaultCatalog } from './shared'

const { t } = useI18n()

const model = defineModel<boolean>({ required: true }) // controls visibility from parent

const props = defineProps<{
	riskMetaId: number
}>()

const name = ref('')
const code = ref('')
const description = ref('')
const catalog = ref<RiskCatalogMeta>(newDefaultCatalog())

const canSave = computed(() => name.value && code.value && description.value && catalog.value?.id)
const emit = defineEmits<{
	confirm: [payload: ThreatMeta]
}>()

function reset() {
	name.value = ''
	code.value = ''
	description.value = ''
	catalog.value = newDefaultCatalog()
}

watch(model, (visible) => {
	// reset fields when modal is closed
	if (!visible) reset()
})

function onConfirm() {
	if (!canSave.value) return
	const payload = {
		threat: {
			name: name.value,
			code: code.value,
			description: description.value,
		} as Threat,
		catalogMeta: catalog.value,
	} as ThreatMeta

	emit('confirm', payload)
}
</script>

<template>
	<Modal v-model="model" type="add-threat-modal" :data-test="'add-threat-modal'">
		<template #header>
			<h5>{{ t('threat.addThreat') }}</h5>
		</template>
		<template #body>
			<div class="mb-2">
				<input
					v-model="name"
					:data-test="'new-threat-name-' + props.riskMetaId"
					class="form-control mb-2"
					:class="{
						'is-invalid': (code.length || catalog.id || description.length) && !name.length,
					}"
					:placeholder="t('threat.addName')"
				/>
				<input
					v-model="code"
					:data-test="'new-threat-code-' + props.riskMetaId"
					class="form-control mb-2"
					:class="{
						'is-invalid': (name.length || catalog.id || description.length) && !code.length,
					}"
					:placeholder="t('threat.addCode')"
				/>
				<input
					v-model="description"
					:data-test="'new-threat-description-' + props.riskMetaId"
					class="form-control mb-2"
					:class="{
						'is-invalid': (name.length || catalog.id || code.length) && !description.length,
					}"
					:placeholder="t('threat.addDescription')"
				/>
				<RiskCatalogSelect
					:data-test="'new-threat-catalog-' + props.riskMetaId"
					:multi-select="false"
					v-model="catalog"
					:class="{
						'is-invalid': !!((name.length || code.length || description.length) && !catalog?.id),
					}"
					:allow-empty="false"
				/>
			</div>
		</template>
		<template #footer>
			<button :data-test="'cancel-add-threat'" type="button" class="btn btn-secondary" data-bs-dismiss="modal">
				{{ $t('misc.cancel') }}
			</button>
			<button :data-test="'confirm-add-threat'" type="button" class="btn btn-primary" :disabled="!canSave" @click="onConfirm">
				{{ $t('misc.confirm') }}
			</button>
		</template>
	</Modal>
</template>
